import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useCategorySEO } from '../components/SEO';
import {
  UserHeader, UserBannerCarousel, UserStatsGrid, UserFilters,
  UserCouponGrid, UserClaims, UserTestimonials,
  UserReferralSection, UserBecomeMerchant, UserNotificationsDialog
} from '../components/user';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function UserDashboard() {
  const navigate = useNavigate();
  const claimsSectionRef = useRef(null);

  // Core data
  const [user, setUser] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [myClaims, setMyClaims] = useState([]);
  const [stats, setStats] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState();
  const [stateFilter, setStateFilter] = useState();
  const [categoryFilter, setCategoryFilter] = useState();
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  useCategorySEO(categoryFilter);

  // Banners
  const [dynamicBanners, setDynamicBanners] = useState([]);
  const [bannerIndex, setBannerIndex] = useState(0);

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  // Dialogs
  const [showReferralDialog, setShowReferralDialog] = useState(false);
  const [showBecomeMerchant, setShowBecomeMerchant] = useState(false);

  // === Effects ===
  useEffect(() => {
    if (dynamicBanners.length === 0) return;
    const timer = setInterval(() => setBannerIndex(prev => (prev + 1) % dynamicBanners.length), 5000);
    return () => clearInterval(timer);
  }, [dynamicBanners]);

  useEffect(() => { fetchData(); window.scrollTo(0, 0); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams();
    if (user.state) params.append('state', user.state);
    if (user.city) params.append('city', user.city);
    if (categoryFilter && categoryFilter !== 'all') params.append('category', categoryFilter);
    axios.get(`${BACKEND_URL}/api/banners?${params.toString()}`)
      .then(res => { setDynamicBanners(res.data || []); setBannerIndex(0); })
      .catch(() => { setDynamicBanners([]); });
  }, [categoryFilter, user]);

  // === Data Fetching ===
  const fetchData = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = { Authorization: `Bearer ${token}` };
      const [userRes, couponsRes, claimsRes, statsRes, testimonialsRes, notifCountRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { headers }),
        axios.get(`${BACKEND_URL}/api/coupons`, { headers }),
        axios.get(`${BACKEND_URL}/api/my-claims`, { headers }),
        axios.get(`${BACKEND_URL}/api/stats`, { headers }),
        axios.get(`${BACKEND_URL}/api/validations`),
        axios.get(`${BACKEND_URL}/api/notifications/count`, { headers }).catch(() => ({ data: { count: 0 } })),
      ]);
      const userData = userRes.data;
      setUser(userData);
      setCoupons(couponsRes.data);
      setMyClaims(claimsRes.data);
      setStats(statsRes.data);
      setTestimonials(testimonialsRes.data);
      setUnreadCount(notifCountRes.data.count);
      if (userData.city) setCityFilter(userData.city);
      if (userData.state) setStateFilter(userData.state);
    } catch (error) {
      // error handled by toast
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // === Handlers ===
  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
      localStorage.removeItem('session_token');
      navigate('/');
    } catch (error) { /* logout error non-blocking */ }
  };

  const handleOpenNotifications = async () => {
    setShowNotifications(true);
    const token = localStorage.getItem('session_token');
    const headers = { Authorization: `Bearer ${token}` };
    const res = await axios.get(`${BACKEND_URL}/api/notifications`, { headers });
    setNotifications(res.data);
    await axios.post(`${BACKEND_URL}/api/notifications/read`, {}, { headers });
    setUnreadCount(0);
  };

  // === Derived Data ===
  const cityStateMap = {};
  coupons.forEach(c => { if (c.city && c.state) cityStateMap[c.city] = c.state; });
  const states = [...new Set(coupons.map(c => c.state).filter(Boolean))].sort();
  const cities = [...new Set(
    coupons.filter(c => !stateFilter || stateFilter === 'all' || c.state === stateFilter).map(c => c.city).filter(Boolean)
  )].sort();

  const handleStateChange = (val) => {
    setStateFilter(val);
    if (val && val !== 'all' && cityFilter && cityFilter !== 'all') {
      if (cityStateMap[cityFilter] !== val) setCityFilter('all');
    }
  };

  const handleCityChange = (val) => {
    setCityFilter(val);
    if (val && val !== 'all' && cityStateMap[val]) setStateFilter(cityStateMap[val]);
  };

  const filteredCoupons = coupons.filter(coupon => {
    const matchesSearch = coupon.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         coupon.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCity = !cityFilter || cityFilter === 'all' || coupon.city === cityFilter;
    const matchesCategory = !categoryFilter || categoryFilter === 'all' || coupon.category === categoryFilter;
    const matchesState = !stateFilter || stateFilter === 'all' || coupon.state === stateFilter;
    return matchesSearch && matchesCity && matchesCategory && matchesState;
  }).sort((a, b) => {
    if (sortBy === 'highest_discount') return b.discount_percentage - a.discount_percentage;
    if (sortBy === 'lowest_discount') return a.discount_percentage - b.discount_percentage;
    if (sortBy === 'expiring_soon') return new Date(a.expires_at) - new Date(b.expires_at);
    return 0;
  });

  const isClaimValid = (claim) => {
    if (!claim.coupon) return false;
    if (claim.is_validated) return false;
    if (claim.coupon.is_active === false) return false;
    if (new Date(claim.coupon.expires_at) < new Date()) return false;
    if (new Date(claim.expires_at) < new Date()) return false;
    return true;
  };

  const validClaims = myClaims.filter(isClaimValid);
  const levelProgress = user ? (user.gamification_points % 100) : 0;

  // === Loading ===
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-slate-500"></div>
          <p className="mt-4 text-slate-600">Carregando...</p>
        </div>
      </div>
    );
  }

  // === Render ===
  return (
    <div className="min-h-screen bg-slate-50">
      <UserHeader user={user} unreadCount={unreadCount} onOpenNotifications={handleOpenNotifications} onLogout={handleLogout} navigate={navigate} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <UserBannerCarousel dynamicBanners={dynamicBanners} bannerIndex={bannerIndex} setBannerIndex={setBannerIndex} />

        <UserStatsGrid user={user} stats={stats} levelProgress={levelProgress} claimsSectionRef={claimsSectionRef} navigate={navigate} />

        <UserFilters
          searchTerm={searchTerm} setSearchTerm={setSearchTerm}
          stateFilter={stateFilter} onStateChange={handleStateChange}
          cityFilter={cityFilter} onCityChange={handleCityChange}
          categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter}
          sortBy={sortBy} setSortBy={setSortBy}
          states={states} cities={cities}
        />

        <UserCouponGrid filteredCoupons={filteredCoupons} viewMode={viewMode} setViewMode={setViewMode} />

        <UserReferralSection showDialog={showReferralDialog} setShowDialog={setShowReferralDialog} />

        <UserNotificationsDialog open={showNotifications} onOpenChange={setShowNotifications} notifications={notifications} />

        <UserClaims validClaims={validClaims} claimsSectionRef={claimsSectionRef} />

        <UserTestimonials testimonials={testimonials} />

        <UserBecomeMerchant showDialog={showBecomeMerchant} setShowDialog={setShowBecomeMerchant} navigate={navigate} />
      </div>
    </div>
  );
}
